import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Pencil, Trash2 } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import RichTextEditor from "@/components/RichTextEditor";
import FileUpload from "@/components/FileUpload";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import AdminCard from "./AdminCard";

export default function WiadomosciAdmin() {
    const [items, setItems] = useState([]);
    const [form, setForm] = useState({ title: "", content: "", category: "aktualnosci", image_url: "" });
    const [editingId, setEditingId] = useState(null);
    const [loading, setLoading] = useState(false);
    const resetForm = () => { setForm({ title: "", content: "", category: "aktualnosci", image_url: "" }); setEditingId(null); };
    const load = () => api.get("/news").then((r) => setItems(r.data || []));
    useEffect(() => { load(); }, []);

    const submit = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            if (editingId) {
                await api.put(`/news/${editingId}`, form);
                toast.success("Wiadomość zaktualizowana.");
            } else {
                await api.post("/news", form);
                toast.success("Wiadomość dodana.");
            }
            resetForm();
            await load();
        } catch (err) {
            toast.error(formatApiErrorDetail(err.response?.data?.detail));
        } finally { setLoading(false); }
    };

    const startEdit = (it) => {
        setForm({ title: it.title, content: it.content, category: it.category || "aktualnosci", image_url: it.image_url || "" });
        setEditingId(it.id);
        window.scrollTo({ top: 0, behavior: "smooth" });
    };

    const del = async (id) => {
        if (!window.confirm("Usunąć wiadomość?")) return;
        await api.delete(`/news/${id}`);
        if (editingId === id) resetForm();
        await load();
    };

    return (
        <div className="grid lg:grid-cols-2 gap-6">
            <AdminCard>
                <h3 className="font-serif text-xl text-[#092E22] mb-5">{editingId ? "Edycja wiadomości" : "Nowa wiadomość"}</h3>
                <form onSubmit={submit} className="space-y-4" data-testid="admin-news-form">
                    <div className="space-y-2"><Label>Tytuł *</Label><Input required value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="admin-news-title" /></div>
                    <div className="space-y-2">
                        <Label>Treść *</Label>
                        <RichTextEditor
                            value={form.content}
                            onChange={(v) => setForm({ ...form, content: v })}
                            placeholder="Treść wiadomości..."
                            testid="admin-news-content"
                        />
                    </div>
                    <div className="space-y-2">
                        <Label>Kategoria</Label>
                        <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                            <SelectTrigger data-testid="admin-news-category"><SelectValue /></SelectTrigger>
                            <SelectContent>
                                <SelectItem value="aktualnosci">Aktualności</SelectItem>
                                <SelectItem value="wydarzenia">Wydarzenia</SelectItem>
                                <SelectItem value="remonty">Remonty</SelectItem>
                                <SelectItem value="organizacyjne">Organizacyjne</SelectItem>
                            </SelectContent>
                        </Select>
                    </div>
                    <div className="space-y-2">
                        <Label>Zdjęcie główne (opcjonalnie)</Label>
                        <FileUpload
                            accept=".jpg,.jpeg,.png,.webp"
                            label="Wgraj zdjęcie"
                            testid="admin-news-image-upload"
                            onUploaded={(d) => setForm((f) => ({ ...f, image_url: d.url }))}
                        />
                        <Input value={form.image_url} onChange={(e) => setForm({ ...form, image_url: e.target.value })} placeholder="...lub wklej URL" data-testid="admin-news-image" className="text-xs" />
                    </div>
                    <div className="flex items-center gap-3">
                        <Button type="submit" disabled={loading} data-testid="admin-news-submit" className="bg-[#0D4F3C] hover:bg-[#092E22] text-white rounded-full px-6">
                            {loading ? "Zapisywanie..." : (editingId ? "Zapisz zmiany" : "Dodaj wiadomość")}
                        </Button>
                        {editingId && (
                            <Button type="button" variant="outline" onClick={resetForm} data-testid="admin-news-cancel" className="rounded-full">Anuluj</Button>
                        )}
                    </div>
                </form>
            </AdminCard>

            <AdminCard>
                <h3 className="font-serif text-xl text-[#092E22] mb-5">Lista wiadomości ({items.length})</h3>
                <div className="space-y-3 max-h-[600px] overflow-y-auto">
                    {items.length === 0 && <p className="text-gray-500">Brak wiadomości.</p>}
                    {items.map((it) => (
                        <div key={it.id} className={`p-4 rounded-2xl bg-[#FAF8F3] border ${editingId === it.id ? "border-[#D4A537]" : "border-gray-100"}`} data-testid={`admin-news-item-${it.id}`}>
                            <div className="flex justify-between items-start gap-3 mb-1">
                                <Badge variant="secondary" className="text-[10px] uppercase">{it.category}</Badge>
                                <div className="flex items-center gap-1">
                                    <button onClick={() => startEdit(it)} className="text-[#0D4F3C] hover:text-[#092E22] p-1.5 rounded-lg hover:bg-white" data-testid={`admin-news-edit-${it.id}`} title="Edytuj"><Pencil size={14} /></button>
                                    <button onClick={() => del(it.id)} className="text-red-500 hover:text-red-700 p-1.5 rounded-lg hover:bg-white" data-testid={`admin-news-delete-${it.id}`} title="Usuń"><Trash2 size={14} /></button>
                                </div>
                            </div>
                            <p className="font-serif text-base text-[#092E22]">{it.title}</p>
                        </div>
                    ))}
                </div>
            </AdminCard>
        </div>
    );
}
